import { deleteFont, makeFont, withPaint } from '@ck';
import type { Color, ColorMode } from '@color';
import {
  HAlignment,
  Point,
  Rect,
  rect,
  Size,
  size,
  VAlignment,
  type Alignment
} from '@geom';
import { canvasKit, fontMgr } from '@resources';
import { TextDirection } from '@strings';
import { expectDefined } from '@utils';
import type { Canvas, TextDirection as CKTextDirection, Font } from 'canvaskit';
import { type FontSizeChooser } from './FontSizeChooser';
import { Paragraph } from './Paragraph';
import type { ParagraphCache } from './ParagraphCache';
import { BasicTextStyle } from './BasicTextStyle';

export function textDirectionToCKTextDirection(
  textDirection: TextDirection
): CKTextDirection {
  switch (textDirection) {
    case TextDirection.LTR:
      return canvasKit.TextDirection.LTR;
    case TextDirection.RTL:
      return canvasKit.TextDirection.RTL;
  }
}

export function ckTextDirectionToTextDirection(
  ckTextDirection: CKTextDirection
): TextDirection {
  switch (ckTextDirection) {
    case canvasKit.TextDirection.LTR:
    default:
      return TextDirection.LTR;
    case canvasKit.TextDirection.RTL:
      return TextDirection.RTL;
  }
}

export function textDirectionForParagraph(paragraph: Paragraph): TextDirection {
  const lineMetrics = paragraph.getLineMetrics();
  const lastLineMetrics = lineMetrics[lineMetrics.length - 1];
  const endIndex = lastLineMetrics.endIndex;
  return ckTextDirectionToTextDirection(
    paragraph.getRectsForRange(
      0,
      endIndex,
      canvasKit.RectHeightStyle.Tight,
      canvasKit.RectWidthStyle.Tight
    )[0].dir
  );
}

export interface ParagraphOpts {
  maxLines: number;
  basicTextStyle?: BasicTextStyle;
}

export function paragraphBounds(paragraph: Paragraph, text: string): Rect {
  const r = paragraph.getRectsForRange(
    0,
    text.length,
    canvasKit.RectHeightStyle.Tight,
    canvasKit.RectWidthStyle.Tight
  );
  return rect(r[0].rect);
}
export const WIDTH_EPSILON = 2;

export interface MeasuredText {
  text: string;
  fontSize: number;
  size: Size;
  opts: Partial<ParagraphOpts>;
  paragraphId: string;
}

export function cloneMeasuredText(measured: MeasuredText): MeasuredText {
  return {
    text: measured.text,
    fontSize: measured.fontSize,
    size: measured.size, // Size is immutable
    opts: { ...measured.opts }, // Shallow clone of opts
    paragraphId: measured.paragraphId
  };
}

export interface LayoutTextOptions {
  fontSizeChooser: FontSizeChooser;
  minWidth: number;
  maxWidth: number;
  aspectRatio: number;
  maxLines?: number;
  basicTextStyle?: BasicTextStyle;
}

export function layoutTextOptionsToJSON(options: Partial<LayoutTextOptions>): unknown {
  return {
    fontSizeChooser: options.fontSizeChooser?.toJSON(),
    minWidth: options.minWidth,
    maxWidth: options.maxWidth,
    aspectRatio: options.aspectRatio,
    maxLines: options.maxLines,
    basicTextStyle: options.basicTextStyle
  };
}

export function cloneLayoutTextOptions(
  options: Partial<LayoutTextOptions>
): Partial<LayoutTextOptions> {
  const cloned: Partial<LayoutTextOptions> = {};
  if (options.fontSizeChooser) {
    cloned.fontSizeChooser = options.fontSizeChooser.clone();
  }
  if (options.minWidth !== undefined) {
    cloned.minWidth = options.minWidth;
  }
  if (options.maxWidth !== undefined) {
    cloned.maxWidth = options.maxWidth;
  }
  if (options.aspectRatio !== undefined) {
    cloned.aspectRatio = options.aspectRatio;
  }
  if (options.maxLines !== undefined) {
    cloned.maxLines = options.maxLines;
  }
  if (options.basicTextStyle !== undefined) {
    cloned.basicTextStyle = options.basicTextStyle;
  }
  return cloned;
}

export function measureSimpleText(str: string, font: Font): Size {
  const glyphIDs: Uint16Array = font.getGlyphIDs(str);
  const glyphCount = glyphIDs.length;
  const glyphBounds: Float32Array = font.getGlyphBounds(glyphIDs);
  let width = 0;
  let height = 0;
  for (let i = 0; i < glyphCount; i++) {
    const index = i * 4;
    const left = glyphBounds[index];
    const top = glyphBounds[index + 1];
    const right = glyphBounds[index + 2];
    const bottom = glyphBounds[index + 3];
    const glyphWidth = right - left;
    const glyphHeight = bottom - top;
    width += glyphWidth;
    height = Math.max(height, glyphHeight);
  }
  return size(width, height);
}

export function drawSimpleText(
  canvas: Canvas,
  text: string,
  p: Point,
  fontSize: number,
  color: Color,
  backgroundColor?: Color,
  backgroundMargin = 5,
  alignment: Alignment = { h: HAlignment.CENTER, v: VAlignment.CENTER }
) {
  const familyName = fontMgr.getFamilyName(0);
  const typeface = fontMgr.matchFamilyStyle(familyName, {});
  const font = makeFont(typeface, fontSize);
  const s = measureSimpleText(text, font);
  const bounds = rect({ center: p, size: s }).alignedRelativeToPoint(p, alignment);

  withPaint(paint => {
    if (backgroundColor) {
      paint.setStyle(canvasKit.PaintStyle.Fill);
      paint.setColor(backgroundColor.ckColor);
      canvas.drawRect(bounds.expandingBy(backgroundMargin).ckRect, paint);
    }
    paint.setColor(color.ckColor);
    canvas.drawText(text, bounds.left, bounds.bottom, paint, font);
  });
  deleteFont(font);
}

export function getParagraph(
  paragraphCache: ParagraphCache,
  measuredText: MeasuredText,
  colorMode: ColorMode
): Paragraph {
  const paragraphId = measuredText.paragraphId;
  const paragraph = expectDefined(paragraphCache.get(paragraphId, colorMode));
  return paragraph;
}

export function paragraphWidth(paragraph: Paragraph): number {
  let maxWidth = 0;
  for (const line of paragraph.lineMetrics) {
    if (line.width > maxWidth) {
      maxWidth = line.width;
    }
  }
  return maxWidth;
}

export function paragraphNumberOfLines(paragraph: Paragraph): number {
  return paragraph.lineMetrics.length;
}
